import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {Autocomplete, Box, Button, Divider, Stack, Tab, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {TabContext} from "@mui/lab";
import TextField from "@mui/material/TextField";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {useAllContentCategories} from "crqlar-react-common/dist/utils/hooks/content/useContentCategories";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ContentService} from "crqlar-react-common/dist/services/content/content.service";
import {Content, ContentType} from "crqlar-react-common/dist/models/content/content.model";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import {ContentCategory, ContentStatus} from "crqlar-react-common/dist/models/content/contentCategory.model";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import ContentDataTagsEditor from "crqlar-react-common/dist/components/ContentDataTagsEditor";
import LocalizedContentEditor from "crqlar-react-common/dist/components/LocalizedContentEditor";
import ExternalInfoPreview from "../common/ExternalInfoPreview";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import ImageUploadField, {
    ALL_IMAGE_UPLOAD_RATIOS
} from "crqlar-react-common/dist/components/ImageUploadField";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";

const ContentDetails = () => {

    //region State

    useTrackScreenVisit('system content details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const readOnly = useReadOnly()
    const categories = useAllContentCategories()
    const activeTab = resolveTab(searchParams.get('tab'), ['basic', 'dataTags', 'localizedContent'], 'basic')
    const setActiveTab = (newTab: string) => setSearchParams({tab: newTab})

    const contentData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return ContentService.getContentById(id)
            } else {
                return {}
            }
        },
        create: ContentService.createContent,
        update: ContentService.updateContent,
        delete: (id) => ContentService.deleteContent({id: [id]}),
        onDeleted: () => navigate('/system/contents', {replace: true}),
        onSaved: (content, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/system/contents/${content.id}`, {replace: true})
            }
        },
        isValid: (content) => {
            return !!content.name
        },
        cleanupBeforeSave: (content) => {
            return {
                ...content,
                name: content.name?.trim()
            }
        },
        canDuplicate: true,
        duplicate: (content) => ContentService.createContent({
            ...content,
            name: `${content.name || ''} (${t('Duplicate')})`.trim()
        }),
        onDuplicate: (content) => {
            window.open(`/system/contents/${content.id}`, '_blank')
        }
    }, id)

    const content = contentData.item
    const setContent = contentData.setItem
    const nameMissing = isRequiredTextMissing(contentData, content.name)


    //endregion Sate

    //region Handlers

    const updateField = (field: keyof Content, value: any) => {
        setContent({...content, [field]: value})
    }

    //endregion Handlers

    //region UI

    const sectionTitleSx = {
        fontSize: {xs: '18px', sm: '20px'},
        lineHeight: 1.25,
    }
    const fieldGroupGap = {xs: 1.5, sm: 1}
    const sectionDividerSx = {
        display: {xs: 'none', sm: 'block'},
    }
    const constrainedSectionSx = {
        width: '100%',
        maxWidth: '900px',
        mx: 'auto',
    }

    const duplicateAction = !readOnly && contentData.canDuplicate ? (
        <Button
            id={'btn-entity-duplicate'}
            variant={'outlined'}
            startIcon={<ContentCopyIcon/>}
            disabled={contentData.saving}
            onClick={() => contentData.duplicate()}
        >
            {t('Duplicate')}
        </Button>
    ) : null

    const headerSection = (
        <SettingsEntityHeader
            title={content.name}
            data={contentData}
            back={() => navigate('/system/contents')}
            extraActions={duplicateAction}
        />
    )

    const imageSection = (
        <ImageUploadField
            id={'image-picker'}
            image={content.media}
            setImage={(image) => updateField('media', image)}
            ratio={'landscape'}
            ratioOptions={ALL_IMAGE_UPLOAD_RATIOS}
            disabled={readOnly}
        />
    )

    const basicInfoSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Basic info')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display title for this content item in guest-facing and internal content lists.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={content.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                <Property
                    title={t('Tag')}
                    tooltip={t('Short grouping key used to identify this content item in apps and integrations.')}
                >
                    <TextField
                        id={'tag'}
                        size={'small'}
                        value={content.tag}
                        onChange={(e) => updateField('tag', e.target.value)}
                        fullWidth
                        required
                    />
                </Property>

                <Property
                    title={t('Status')}
                    tooltip={t('Controls whether this content is active or still draft.')}
                >
                    <NewStatusPicker<ContentStatus>
                        value={content.status || ContentStatus.DRAFT}
                        onChange={(value) => updateField('status', value)}
                        ariaLabel={t('Status')}
                        options={[
                            {value: ContentStatus.DRAFT, label: t('Draft')},
                            {value: ContentStatus.ACTIVE, label: t('Active')},
                        ]}
                    />
                </Property>

                <Property
                    title={t('Categories')}
                    tooltip={t('Content categories where this item should appear.')}
                >
                    <Autocomplete
                        id={'categories'}
                        multiple
                        options={categories}
                        getOptionLabel={(option: ContentCategory) => option.name || ''}
                        value={content.categoryIds?.map(id => categories.find(c => c.id === id)).filter(Boolean) as ContentCategory[] || []}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                        fullWidth
                        onChange={(e, value) => {
                            updateField('categoryIds', value.map((v: any) => v.id))
                        }}
                    />
                </Property>

                <Property
                    title={t('Content type')}
                    tooltip={t('Defines whether this is a regular post or app data used by guest-facing screens.')}
                >
                    <ToggleButtonGroup
                        id={'btn-status'}
                        value={content.type || ContentType.POST}
                        exclusive
                        onChange={(e, value) => updateField('type', value)}
                        size={'small'}
                    >
                        <ToggleButton
                            id={'btn-content-type-post'}
                            value={ContentType.POST}>
                            {t('Post')}
                        </ToggleButton>
                        <ToggleButton
                            id={'btn-content-type-app-data'}
                            value={ContentType.APP_DATA}>
                            {t('App data')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Property>

                <Property
                    title={t('Order priority')}
                    tooltip={t('Lower or higher priority can be used by content surfaces to order items.')}
                >
                    <TextField
                        id={'order-priority'}
                        size={'small'}
                        value={content.orderPriority}
                        fullWidth
                        type={'number'}
                        onChange={(e) => {
                            updateField('orderPriority', parseInt(e.target.value))
                        }}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const translationsSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Translations')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                <Property
                    title={t('Name translations')}
                    tooltip={t('Localized content names shown in translated interfaces.')}
                >
                    <LocalizedTextField
                        id={'nameLocalized'}
                        size={'small'}
                        value={content.nameLocalized}
                        onChange={(e) => updateField('nameLocalized', e)}
                        fullWidth
                        required
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const externalInfoSection = (
        !!content.externalInfo?.length && <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('External info')}
            </Typography>
            <ExternalInfoPreview externalInfo={content.externalInfo}
                                 setExternalInfo={e => updateField('externalInfo', e)}/>
        </Stack>
    )

    const basicInfoTab = (
        <Stack gap={{xs: 5, sm: 6}}>
            {imageSection}
            {basicInfoSection}
            <Divider sx={sectionDividerSx}/>
            {translationsSection}
            {
                externalInfoSection &&
                <>
                    <Divider sx={sectionDividerSx}/>
                    {externalInfoSection}
                </>
            }
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete content')}
                description={t('This permanently deletes the content item. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={contentData.saving}
                disabled={readOnly}
                onDelete={() => contentData.removeNow()}
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {content.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const basicInfoContent = (
        <Stack gap={{xs: 5, sm: 6}}>
            {basicInfoTab}

            {
                !!content.id &&
                <>
                    <Divider sx={sectionDividerSx}/>
                    {dangerZoneSection}
                </>
            }
        </Stack>
    )

    const dataTagsTab = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Data Tags')}
            </Typography>

            <ContentDataTagsEditor
                data={content}
                setData={setContent}
            />
        </Stack>
    )

    const localizedContentTab = (
        <Stack gap={{xs: 2.5, sm: 2}} width={'100%'}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Localized Content')}
            </Typography>

            <LocalizedContentEditor
                value={content?.contentSource}
                onChange={contentSource => setContent({
                    ...content,
                    contentSource
                })}
                isDirty={contentData.isDirty}
            />
        </Stack>
    )

    const tabsSection = (
        <TabContext value={activeTab}>
            <ScrollableTabList onChange={(e, value) => setActiveTab(value)}>
                <Tab
                    iconPosition="start"
                    label={t('Basic info')}
                    value={'basic'}
                    id="basic-info-tab"
                    aria-controls="basic-info-tabpanel"
                />
                <Tab
                    iconPosition="start"
                    label={t('Data Tags')}
                    value={'dataTags'}
                    id="data-tags-tab"
                    aria-controls="data-tags-tabpanel"
                />
                <Tab
                    iconPosition="start"
                    label={t('Localized Content')}
                    value={'localizedContent'}
                    id="localized-content-tab"
                    aria-controls="localized-content-tabpanel"
                />
            </ScrollableTabList>
        </TabContext>
    )

    //endregion UI

    const contentDataUI = (
        <Stack
            sx={{
                width: '100%',
                mt: 3,
                gap: 6,
            }}
        >
            <Box sx={constrainedSectionSx}>
                {tabsSection}
            </Box>

            {activeTab === 'basic' && (
                <Box
                    role="tabpanel"
                    id="basic-info-tabpanel"
                    aria-labelledby="basic-info-tab"
                    sx={constrainedSectionSx}
                >
                    {basicInfoContent}
                </Box>
            )}

            {activeTab === 'dataTags' && (
                <Box
                    role="tabpanel"
                    id="data-tags-tabpanel"
                    aria-labelledby="data-tags-tab"
                    sx={constrainedSectionSx}
                >
                    {dataTagsTab}
                </Box>
            )}

            {activeTab === 'localizedContent' && (
                <Box
                    role="tabpanel"
                    id="localized-content-tabpanel"
                    aria-labelledby="localized-content-tab"
                >
                    {localizedContentTab}
                </Box>
            )}
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}
            {contentDataUI}
        </Box>
    );
}

export default ContentDetails;
